/**
 * Form Validation Utilities
 *
 * This file provides validation functions for authentication forms.
 * All functions return null if valid, or an error message string if invalid.
 *
 * @fileoverview Validation utilities for email, password, and name fields
 *
 * @example
 * import { validateEmail, validatePassword } from '../utils/validation';
 *
 * const emailError = validateEmail('user@example.com');
 * if (emailError) {
 *   console.error(emailError); // null if valid
 * }
 */

/**
 * Email validation regex pattern
 * Matches standard email format: local@domain.tld
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Password requirements regex patterns
 */
const HAS_UPPERCASE = /[A-Z]/;
const HAS_NUMBER = /[0-9]/;
const MIN_PASSWORD_LENGTH = 8;
const MIN_NAME_LENGTH = 2;

/**
 * E.164 phone number format regex
 * Matches: +[country code][number]
 * Example: +15551234567
 */
const E164_REGEX = /^\+[1-9]\d{1,14}$/;

/**
 * Validates an email address
 *
 * @param email - The email address to validate
 * @returns Error message if invalid, null if valid
 *
 * @example
 * validateEmail(''); // 'Email is required'
 * validateEmail('invalid'); // 'Please enter a valid email address'
 * validateEmail('user@example.com'); // null
 */
export const validateEmail = (email: string): string | null => {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }

  if (!EMAIL_REGEX.test(email.trim())) {
    return 'Please enter a valid email address';
  }

  return null;
};

/**
 * Validates a password for strength requirements
 *
 * Requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one number
 *
 * @param password - The password to validate
 * @returns Error message if invalid, null if valid
 *
 * @example
 * validatePassword(''); // 'Password is required'
 * validatePassword('short'); // 'Password must be at least 8 characters'
 * validatePassword('longenough'); // 'Password must contain at least one uppercase letter'
 * validatePassword('Longenough'); // 'Password must contain at least one number'
 * validatePassword('Password1'); // null
 */
export const validatePassword = (password: string): string | null => {
  if (!password || password === '') {
    return 'Password is required';
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
  }

  if (!HAS_UPPERCASE.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }

  if (!HAS_NUMBER.test(password)) {
    return 'Password must contain at least one number';
  }

  return null;
};

/**
 * Validates a display name
 *
 * Requirements:
 * - Required field
 * - Minimum 2 characters
 *
 * @param name - The name to validate
 * @param fieldLabel - Optional custom field label for error messages (default: 'Name')
 * @returns Error message if invalid, null if valid
 *
 * @example
 * validateName(''); // 'Name is required'
 * validateName('A'); // 'Name must be at least 2 characters'
 * validateName('John'); // null
 * validateName('', 'First name'); // 'First name is required'
 */
export const validateName = (name: string, fieldLabel: string = 'Name'): string | null => {
  if (!name || name.trim() === '') {
    return `${fieldLabel} is required`;
  }

  if (name.trim().length < MIN_NAME_LENGTH) {
    return `${fieldLabel} must be at least ${MIN_NAME_LENGTH} characters`;
  }

  return null;
};

/**
 * Validates a phone number — accepts US 10-digit numbers or E.164 format.
 * 10-digit numbers are treated as US (+1) by default.
 *
 * @param phone - The phone number to validate (optional field)
 * @returns Error message if invalid format, null if valid or empty
 *
 * @example
 * validatePhoneE164(''); // null (optional field)
 * validatePhoneE164('5551234567'); // null (US number, +1 assumed)
 * validatePhoneE164('555 123 4567'); // null
 * validatePhoneE164('+15551234567'); // null
 */
export const validatePhoneE164 = (phone: string): string | null => {
  if (!phone || phone.trim() === '') {
    return null;
  }

  const digits = phone.replace(/\D/g, '');

  // 10-digit US number (no country code)
  if (digits.length === 10 && !phone.trim().startsWith('+')) {
    return null;
  }

  // 11-digit starting with 1 (US with country code, with or without +)
  if (digits.length === 11 && digits.startsWith('1')) {
    return null;
  }

  // Full E.164 with + prefix
  const cleaned = phone.replace(/\s/g, '');
  if (E164_REGEX.test(cleaned)) {
    return null;
  }

  return 'Enter a 10-digit US number (e.g., 555 123 4567) or international format (e.g., +44 20 1234 5678)';
};

/**
 * Formats a phone number to E.164 format.
 * 10-digit numbers are treated as US (+1) by default.
 *
 * @param phone - The phone number to format
 * @returns Formatted phone number in E.164 format, or empty string if no digits
 *
 * @example
 * formatPhoneE164('555 123 4567'); // '+15551234567'
 * formatPhoneE164('+1 (555) 123-4567'); // '+15551234567'
 * formatPhoneE164(''); // ''
 */
export const formatPhoneE164 = (phone: string): string => {
  if (!phone) {
    return '';
  }

  const digits = phone.replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  // 10-digit US number — prepend +1
  if (digits.length === 10 && !phone.trim().startsWith('+')) {
    return `+1${digits}`;
  }

  // Already has + prefix
  if (phone.trim().startsWith('+')) {
    return `+${digits}`;
  }

  // 11 digits starting with 1 (US with country code, no +)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  return `+${digits}`;
};

/**
 * Validates that confirm password matches the original password
 *
 * @param password - The original password
 * @param confirmPassword - The confirmation password to compare
 * @returns Error message if they don't match, null if they match
 *
 * @example
 * validateConfirmPassword('Password1', ''); // 'Please confirm your password'
 * validateConfirmPassword('Password1', 'Different1'); // 'Passwords do not match'
 * validateConfirmPassword('Password1', 'Password1'); // null
 */
export const validateConfirmPassword = (
  password: string,
  confirmPassword: string
): string | null => {
  if (!confirmPassword || confirmPassword === '') {
    return 'Please confirm your password';
  }

  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }

  return null;
};

/**
 * Validates all login form fields
 *
 * @param email - Email address
 * @param password - Password
 * @returns Object with field errors, empty object if all valid
 *
 * @example
 * const errors = validateLoginForm('user@example.com', 'Password1');
 * // { email: null, password: null } or { email: 'Email is required', password: null }
 */
export const validateLoginForm = (
  email: string,
  password: string
): { email: string | null; password: string | null } => {
  return {
    email: validateEmail(email),
    password: password ? null : 'Password is required', // Simpler validation for login
  };
};

/**
 * Validates all sign up form fields
 *
 * @param name - User's full name
 * @param email - Email address
 * @param password - Password
 * @param confirmPassword - Password confirmation
 * @returns Object with field errors
 *
 * @example
 * const errors = validateSignUpForm('John', 'user@example.com', 'Password1', 'Password1');
 */
export const validateSignUpForm = (
  name: string,
  email: string,
  password: string,
  confirmPassword: string
): {
  name: string | null;
  email: string | null;
  password: string | null;
  confirmPassword: string | null;
} => {
  return {
    name: validateName(name),
    email: validateEmail(email),
    password: validatePassword(password),
    confirmPassword: validateConfirmPassword(password, confirmPassword),
  };
};

/**
 * Checks if a form has any validation errors
 *
 * @param errors - Object containing field error messages
 * @returns true if there are errors, false if all valid
 *
 * @example
 * const errors = validateLoginForm('', '');
 * hasErrors(errors); // true
 */
export const hasErrors = (errors: Record<string, string | null>): boolean => {
  return Object.values(errors).some((error) => error !== null);
};
